import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEffect, useState } from 'react';
import { ILegalTransactionRelationship } from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { getEmptyParentMember } from '../../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberParent';
import {
  getEmptyFamilyRelationship,
  IFamilyRelationship,
} from '../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/Family/ILegalTransactionFamilyRelationship';
import {
  FAMILY_RELATIONSHIP_TYPE_ID,
  PARENT_MEMBER_ROLE_ID,
} from '../../../../../../services/relationshipUtils.service';
import { DialogResponsive } from '../../../../../core/DialogResponsive';
import {
  createChildren,
  present,
  TransitionStatus,
  updateOrCreateRelationship,
} from '../../../generic/utilities';
import { CreateChildSlide } from './AddNodeDialog/AddChildSlide';
import { AddPartnerSlide } from './AddNodeDialog/AddPartnerSlide';
import { ChoosePartnerSlide } from './AddNodeDialog/ChoosePartnerSlide';
import { dim } from './myRelationshipToTreeTransformer';
import { Tree, TreeNodeId } from './types';
import { useLegalClientStore } from '../../../generic/legalClientStore';

export type AddNodeDialogConfig = {
  context: AddNodeDialogContext;
  baseNodeId: TreeNodeId;
};

export const AddNodeDialogContext = {
  ChildOnPerson: 'childOnPerson',
  ChildOnRelationship: 'childOnRelationship',
  PartnerOnPerson: 'partnerOnPerson',
} as const;

export type AddNodeDialogContext =
  (typeof AddNodeDialogContext)[keyof typeof AddNodeDialogContext];

function nodeContextToDialogTitle(context: AddNodeDialogContext): string {
  switch (context) {
    case AddNodeDialogContext.ChildOnPerson:
    case AddNodeDialogContext.ChildOnRelationship:
      return 'Kind hinzufügen';
    case AddNodeDialogContext.PartnerOnPerson:
      return 'Partner hinzufügen';
    default:
      throw new Error(`Unknown context: ${context}`);
  }
}

type AddNodeDialogProps = {
  tree: Tree;
  context: AddNodeDialogContext;
  baseNodeId: TreeNodeId;
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  isOpen: boolean;
  onClose: () => void;
};

export const AddNodeDialog: React.FC<AddNodeDialogProps> = ({
  tree,
  context,
  baseNodeId,
  relationshipArray,
  setRelationshipArray,
  isOpen,
  onClose,
}) => {
  const [getLegalClient] = useLegalClientStore(state => [state.getLegalClient]);

  const [currentPage, setCurrentPage] = useState(0);
  const [previousPage, setPreviousPage] = useState(-1);
  const [transitionStatus, setTransitionStatus] = useState<TransitionStatus>(
    TransitionStatus.IDLE
  );

  const [otherPartnerId, setOtherPartnerId] = useState<number | 'new'>('new');

  const [familyRelationshipId, setFamilyRelationshipId] = useState<number>(1);

  const handleBack = () => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage - 1);
    setTransitionStatus(TransitionStatus.EXITING);
  };
  const handleNext = () => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage + 1);
    setTransitionStatus(TransitionStatus.EXITING);
  };

  const onEntered = () => {
    setTransitionStatus(TransitionStatus.IDLE);
  };

  const onExited = () => {
    if (transitionStatus === TransitionStatus.EXITING) {
      setTransitionStatus(TransitionStatus.ENTERING);
    } else {
      setTransitionStatus(TransitionStatus.IDLE);
    }
  };

  useEffect(() => {
    let relationship: ILegalTransactionRelationship | undefined;

    if (context === AddNodeDialogContext.ChildOnRelationship) {
      const marriageRelationship = relationshipArray.find(
        rel => rel.idRelationship === Number(dim(baseNodeId))
      );
      if (marriageRelationship) {
        const marriageMemberIds = marriageRelationship.Members.map(
          m => m.idLegalClient
        );
        relationship = relationshipArray.find(
          rel =>
            rel.idRelationshipType === FAMILY_RELATIONSHIP_TYPE_ID &&
            rel.Members.filter(
              m => m.idRelationshipRole === PARENT_MEMBER_ROLE_ID
            ).every(m => marriageMemberIds.includes(m.idLegalClient))
        );
        if (!relationship) {
          const legalClients = marriageRelationship.Members.map(m =>
            getLegalClient(m.idLegalClient)
          ).filter(present);
          const emptyFamilyRelationship = getEmptyFamilyRelationship();
          const newFamilyRelationship: IFamilyRelationship = {
            ...emptyFamilyRelationship,
            Members: [
              ...legalClients.map(lc =>
                getEmptyParentMember(lc, emptyFamilyRelationship.idRelationship)
              ),
            ],
          };
          setFamilyRelationshipId(newFamilyRelationship.idRelationship);
          setRelationshipArray(
            updateOrCreateRelationship(relationshipArray, newFamilyRelationship)
          );
        }
      }
    } else if (
      context === AddNodeDialogContext.ChildOnPerson &&
      otherPartnerId !== 'new'
    ) {
      relationship = relationshipArray.find(
        rel =>
          rel.idRelationshipType === FAMILY_RELATIONSHIP_TYPE_ID &&
          [Number(baseNodeId), otherPartnerId].every(id =>
            rel.Members.filter(
              m => m.idRelationshipRole === PARENT_MEMBER_ROLE_ID
            ).some(m => m.idLegalClient === id)
          )
      );
    }

    if (relationship) {
      setFamilyRelationshipId(relationship.idRelationship);
    } else if (
      context !== AddNodeDialogContext.PartnerOnPerson &&
      otherPartnerId !== 'new'
    ) {
      setCurrentPage(0);
      setFamilyRelationshipId(1);
    }
  }, [relationshipArray, otherPartnerId]);

  const steps = [
    {
      name: 'Partner auswählen',
      condition: () => context === AddNodeDialogContext.ChildOnPerson,
      content: (
        <ChoosePartnerSlide
          tree={tree}
          setOtherPartnerId={setOtherPartnerId}
          baseParentId={baseNodeId}
          onClose={onClose}
          onNext={handleNext}
        />
      ),
    },
    {
      name: 'Partner hinzufügen',
      condition: () =>
        (
          [
            AddNodeDialogContext.ChildOnPerson,
            AddNodeDialogContext.PartnerOnPerson,
          ] as AddNodeDialogContext[]
        ).includes(context) && otherPartnerId === 'new',
      content: (
        <AddPartnerSlide
          baseParentIdLegalClient={Number(baseNodeId)}
          relationshipArray={relationshipArray}
          setRelationshipArray={setRelationshipArray}
          handleBack={handleBack}
          handleClose={onClose}
          handleNext={
            context === AddNodeDialogContext.PartnerOnPerson
              ? onClose
              : handleNext
          }
          setFamilyRelationshipId={setFamilyRelationshipId}
        />
      ),
    },
    {
      name: 'Kind hinzufügen',
      condition: () =>
        (
          [
            AddNodeDialogContext.ChildOnPerson,
            AddNodeDialogContext.ChildOnRelationship,
          ] as AddNodeDialogContext[]
        ).includes(context),
      content: (
        <CreateChildSlide
          relationshipId={familyRelationshipId}
          relationshipArray={relationshipArray}
          setRelationshipArray={setRelationshipArray}
          handleBack={onClose}
          handleClose={onClose}
          handleNext={onClose}
        />
      ),
    },
  ];

  return (
    <DialogResponsive
      open={isOpen}
      onClose={onClose}
      title={nodeContextToDialogTitle(context)}
      maxWidth='lg'
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
        {createChildren({
          steps,
          currentPage,
          previousPage,
          transitionStatus,
          onEntered,
          onExited,
        })}
      </LocalizationProvider>
    </DialogResponsive>
  );
};
